'use strict';

import 'core-js/features/array/at';

import 'Components/chat';
import 'Components/form/VZInputField';
import VZSpinner from 'Components/VZSpinner';
import VZToast from 'Components/VZToast';
import { toggleButtonState } from 'Root/util/util';
import CONST from 'Util/constants';

(function loginVerification() {
    /**
     * @constant {string} ERROR_CLASS - The class for an error message.
     */
    const ERROR_CLASS = 'js-error';

    /**
     * @constant {Spinner} spinner - The spinner component
     */
    const spinner = document.querySelector('vz-spinner') || new VZSpinner();

    const verificationCodeInputs = [...document.querySelectorAll('.js-verification-code-inputs input')];

    /**
     * @private showError
     * @param {JSON} response - JSON response
     * @param {HTMLFormElement} form - The current form
     */
    function showError(response, form) {
        const existingError = form.querySelector(`.${ERROR_CLASS}`);
        if (existingError) existingError.remove();

        const errorLocation = document.querySelector('.js-verification-code-inputs');
        verificationCodeInputs.forEach((input) => input.classList.add('invalid'));
        errorLocation.insertAdjacentHTML(
            'beforeEnd',
            `
            <div class="${ERROR_CLASS} col-12 text-primary">
                <small>${response.serverErrors[0]}</span>
            </div>
        `
        );
    }

    /**
     * @function updateHiddenVerificationCodeInput
     * @description updates the hidden input field with the concatenated code
     * @param {Event} evt submit event
     */
    function updateHiddenVerificationCodeInput(evt) {
        const { target: form } = evt;
        const hiddenInput = form.querySelector('input[type="hidden"]');
        if (verificationCodeInputs && hiddenInput) {
            const code = verificationCodeInputs.map((input) => input.value).join('');
            hiddenInput.value = code;
        }
    }

    /**
     * @function onSubmit
     * @param {SubmitEvent} event - The form submit event
     */
    function onSubmit(event) {
        event.preventDefault();
        spinner.start();
        updateHiddenVerificationCodeInput(event);

        const form = event.target;
        const submitBtn = form.querySelector('button[type="submit"]');
        submitBtn?.setAttribute(CONST.attributes.disabled, CONST.attributes.disabled);
        submitBtn?.classList.add(CONST.attributes.disabled);
        const formData = new FormData(form);
        const errorMsg = form.getAttribute('data-error-msg');
        formData.append(window.csrf.tokenName, window.csrf.token);

        fetch(event.target.action, {
            method: 'POST',
            body: formData
        })
            .then((resp) => resp.json())
            .then((response) => {
                if (!response.success && response.error) {
                    showError(response, form);
                } else if (response.success && response.redirectUrl) {
                    if (response.placeorder) {
                        // Place order
                        const orderFormData = new FormData();
                        orderFormData.append(window.csrf.tokenName, response.csrf_token || window.csrf.token);
                        fetch(response.redirectUrl, {
                            method: 'POST',
                            body: orderFormData
                        })
                            .then((res) => res.json())
                            .then((res) => {
                                if (res.success) {
                                    window.location.href = res.redirectUrl;
                                } else if (res.redirectUrl) {
                                    window.location.href = res.redirectUrl;
                                } else if (res.serverErrors && res.serverErrors.length > 0) {
                                    showError(res, form);
                                }
                            })
                            .catch((res) => {
                                if (!res?.responseJSON) {
                                    showError({ serverErrors: [errorMsg] }, form);
                                }
                                if (res?.responseJSON?.redirectUrl) {
                                    window.location.href = res.responseJSON.redirectUrl;
                                }
                            })
                            .finally(spinner.stop);
                        return;
                    }
                    if (event.target.dataset.successMsg) {
                        const toast = new VZToast('resend-email');
                        toast.setBodyMessage(event.target.dataset.successMsg);
                        toast.show();
                    } else {
                        window.location.href = response.redirectUrl;
                    }
                }
                submitBtn?.removeAttribute(CONST.attributes.disabled);
                submitBtn?.classList.remove(CONST.attributes.disabled);
                spinner.stop();
            })
            .catch((response) => {
                if (!response?.responseJSON) {
                    showError({ serverErrors: [errorMsg] }, form);
                }
                if (response?.responseJSON?.redirectUrl) {
                    window.location.href = response.responseJSON.redirectUrl;
                }
                submitBtn?.removeAttribute(CONST.attributes.disabled);
                submitBtn?.classList.remove(CONST.attributes.disabled);
                spinner.stop();
            });
    }

    /**
     * @function setVerificationCodeMaxLength
     * @param {Event} evt the input event
     * @description chrome ignores maxlength on input type number so this accounts for that
     */
    function setVerificationCodeMaxLength(evt) {
        const { value, maxLength } = evt.target;

        evt.target.value = value.length > maxLength ? value.slice(0, maxLength) : value;
    }

    /**
     * @function setFocus
     * @description Focusses on the element passed in.
     * @param {HTMLElement} elementToFocus - The element which needs focus.
     */
    function setFocus(elementToFocus) {
        if (elementToFocus) elementToFocus.focus();
    }

    /**
     * @function setFocusNextSibling
     * @param {Event} event - input event
     * @description Sets the focus to the next input field when there is one
     */
    function setFocusNextSibling(event) {
        const { target: input, inputType, data } = event;
        if (inputType === 'deleteContentBackward') return; // Should not execute for this input type.

        const isNewEntry = inputType === 'insertText' && data !== input.value;
        if (isNewEntry) input.value = data; // Updates the input value with the new value if there is already a value present.
        if ((input.value && data === input.value) || isNewEntry) setFocus(input.nextElementSibling);
    }

    /**
     * @function handlePaste
     * @param {Event} event - paste event.
     * @description Pastes the value in each input field and sets the focus on the correct field after paste.
     */
    function handlePaste(event) {
        event.preventDefault();
        const copiedContent = event.clipboardData.getData('text');

        // Loop over copied content and populate input fields with values.
        copiedContent.split('').forEach((copiedCharacter, index) => {
            verificationCodeInputs[index].value = copiedCharacter;
        });

        /**
         * Set focus on the last input which has a value to account for copiedContent where the user did not copy the entire code.
         * If the last input with a value is not the last verification input, it sets the focus on the next empty input.
         */
        const lastInputWithValue = verificationCodeInputs.find((input, index) => index === copiedContent.length - 1 && input);
        if (lastInputWithValue !== verificationCodeInputs.at(-1)) {
            setFocus(lastInputWithValue.nextElementSibling);
        } else {
            setFocus(lastInputWithValue);
        }
    }

    /**
     * @function handleBackspace
     * @description Handles the scenario where the user hits the backspace key by setting focus on the previous element.
     * @param {Event} event - An input event.
     */
    function handleBackspace(event) {
        const { inputType, target } = event;
        if (inputType === 'deleteContentBackward') setFocus(target.previousElementSibling);
    }

    /**
     * @function onInput
     * @param {Event} event - input event
     * @description Enables/disables the form submit button
     */
    function onInput(event) {
        const { currentTarget: form } = event;
        const submitButton = form.querySelector('button');
        toggleButtonState(submitButton, form.checkValidity());
    }

    /**
     * @private init
     */
    (function init() {
        const loginForm = [...document.querySelectorAll('.js-login-form')];

        loginForm.forEach((form) => {
            form.addEventListener('submit', onSubmit);
            form.addEventListener('input', onInput);
            form.addEventListener('paste', onInput); // Handle enabling/disabling on paste as well
        });

        verificationCodeInputs.forEach((input, index) => {
            input.addEventListener('input', setVerificationCodeMaxLength);
            input.addEventListener('input', setFocusNextSibling);
            input.addEventListener('input', handleBackspace);

            if (index === 0) input.addEventListener('paste', handlePaste);
        });
    })();
})();
